import { useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import { Box, IconButton, Switch, lighten, useTheme } from '@mui/material';
import { ReactComponent as ArrowDownOutlinedIcon } from '../../../../assets/icons/main/expand-down-icon.svg';
import { ReactComponent as ArrowUpOutlinedIcon } from '../../../../assets/icons/main/collapse-up-icon.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/main/delete-icon.svg';
import { get } from 'lodash';
import { useOutletContext } from 'react-router-dom';

const Styles = {
  accordionWrapper: {
    mb: 4,
  },
  accordion: {
    boxShadow: 'none',
    '&.MuiAccordion-root:last-of-type': {
      borderBottomLeftRadius: '12px',
      borderBottomRightRadius: '12px',
    },
    '&.MuiAccordion-root:first-of-type': {
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px',
    },
  },
  accordionSummary: {
    display: 'flex',
    padding: '12px 24px',

    '&.Mui-expanded': {
      margin: 0,
      minHeight: 50,

      '&.MuiAccordionSummary-root': {
        borderRadius: '8px 8px 0px 0px',
      },
    },

    '& .MuiAccordionSummary-content.Mui-expanded': {
      margin: 0,
    },

    '& .MuiAccordionSummary-contentGutters': {
      margin: 0,
      alignItems: 'center',
      justifyContent: 'space-between',
    },
  },
  accordionSummaryCollapsed: {
    borderRadius: '8px',
    // height: '50px',
  },
  accordionSummaryExpanded: {
    // borderRadius: '8px 8px 0px 0px',
    borderRadius: '8px',
    '&.Mui-expanded': {
      minHeight: '50px',
    },
  },
  accordionDetails: {
    borderWidth: '0px 1px 1px 1px',
    borderStyle: 'solid',
    borderRadius: '0px 0px 8px 8px',
    padding: 3,
    // marginTop: '-12px',
  },
  summaryWrapper: { display: 'flex', alignItems: 'center', height: 3 },
  titleWrapper: { display: 'flex', mr: 1 },
  arrowIcon: { marginRight: '24px' },
  type: { fontSize: '16px', marginRight: '2px' },
  title: { fontSize: '16px', ml: 1 },
  iconButton: { p: 0, '&.Mui-disabled': { opacity: 0.5 } },
};

interface PropTypes {
  title: string;
  checked?: boolean;
  onCheck?: () => void;
  onChange?: (openOrClose: boolean) => void;
  onDelete?: () => void;
  children: any;
  expanded?: boolean;
  disabled?: boolean;
  hideToggle?: boolean;
  hideDelete?: boolean;
  hasErrors?: boolean;
}

const AccordionComponent = ({
  title,
  checked,
  onCheck = () => {},
  onChange = () => {},
  onDelete = () => {},
  children,
  expanded,
  disabled,
  hideToggle,
  hideDelete,
  hasErrors
}: PropTypes) => {
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);
  const themeData: any = useTheme();
  const themeMode: any = get(useOutletContext(), 'themeMode', {});
  const handleAccordion = () => {
    onChange(!isAccordionExpanded);
    setIsAccordionExpanded(!isAccordionExpanded);
  };

  return (
    <Box sx={Styles.accordionWrapper}>
      <Accordion sx={Styles.accordion} expanded={expanded ?? isAccordionExpanded} onChange={handleAccordion}>
        <AccordionSummary
          aria-controls={title}
          id={title}
          sx={[
            Styles.accordionSummary,
            isAccordionExpanded ? Styles.accordionSummaryExpanded : Styles.accordionSummaryCollapsed,
            {
              backgroundColor: themeMode.isLightBG
                ? lighten(themeData.palette.background.default, 0.2)
                : lighten(themeData.palette.background.default, 0.08),
              border: `1px solid ${
                hasErrors ? themeData.palette.warning.light : themeData.palette.customBorderColor.main
              }`,
            },
          ]}
        >
          <Box sx={Styles.summaryWrapper}>
            <Box sx={Styles.titleWrapper}>
              {isAccordionExpanded ? (
                <ArrowUpOutlinedIcon style={{ ...Styles.arrowIcon, color: themeData.palette.custom.light }} />
              ) : (
                <ArrowDownOutlinedIcon style={{ ...Styles.arrowIcon, color: themeData.palette.custom.light }} />
              )}
              <Typography sx={{ ...Styles.type, color: themeData.palette.text.secondary }}>Type : </Typography>
              <Typography sx={Styles.title}>{title}</Typography>
            </Box>
            {!hideToggle && (
              <Switch
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  e.stopPropagation();
                }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onCheck();
                }}
                checked={checked}
                disabled={disabled}
              />
            )}
          </Box>
          {!hideDelete && (
            <IconButton
              sx={Styles.iconButton}
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                onDelete();
              }}
              disabled={disabled}
            >
              <DeleteIcon style={{ color: themeData.palette.custom.light }} />
            </IconButton>
          )}
        </AccordionSummary>
        <AccordionDetails
          sx={[
            Styles.accordionDetails,
            {
              borderColor: hasErrors ? themeData.palette.warning.light : themeData.palette.customBorderColor.main,
              backgroundColor: themeMode.isLightBG
                ? lighten(themeData.palette.background.default, 0.9)
                : lighten(themeData.palette.background.default, 0.05),
            },
          ]}
        >
          {children}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default AccordionComponent;
